import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './state/query/queryClient';
import { ErrorBoundary } from './error-handling/ErrorBoundary';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { OrderCountsProvider } from './contexts/OrderCountsContext';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';

// Public routes (lazy loaded)
const LoginPage = React.lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const TrackingSearchPage = React.lazy(() => import('./pages/TrackingSearchPage').then(m => ({ default: m.TrackingSearchPage })));
const OrderTrackingPage = React.lazy(() => import('./pages/OrderTrackingPage').then(m => ({ default: m.OrderTrackingPage })));

// Route categories
import { dashboardRoutes } from './routes/dashboardRoutes';
import { orderRoutes } from './routes/orderRoutes';
import { deliveryRoutes } from './routes/deliveryRoutes';
import { adminRoutes } from './routes/adminRoutes';
import { otherRoutes } from './routes/otherRoutes';
import { threeplRoutes } from './routes/threeplRoutes';
import { hubRoutes } from './routes/hubRoutes';

// 3PL client portal renders outside the admin Layout (minimal chrome)
const ThreePLClientPortal = React.lazy(() => import('./pages/threepl/ThreePLClientPortal'));

// Sydney Solvent Enterprise Theme
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1a7a7a',
      light: '#2d9d9d',
      dark: '#135c5c',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#475569',
      light: '#64748b',
      dark: '#334155',
    },
    background: {
      default: '#f1f3f6',
      paper: '#ffffff',
    },
    text: {
      primary: '#1a2332',
      secondary: '#64748b',
    },
    success: { main: '#2d9d78' },
    warning: { main: '#e5930a' },
    error: { main: '#dc3545' },
    info: { main: '#3b82f6' },
    divider: '#e5e8ed',
  },
  typography: {
    fontFamily: '"DM Sans", system-ui, -apple-system, sans-serif',
    h1: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h2: { fontSize: '1.625rem', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h3: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.35 },
    h4: { fontSize: '1.125rem', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: '1rem', fontWeight: 600, lineHeight: 1.4 },
    h6: { fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.5 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.5 },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.5 },
    body1: { fontSize: '0.9375rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.55 },
    button: { fontWeight: 500, letterSpacing: '0' },
    caption: { fontSize: '0.75rem', lineHeight: 1.5, color: '#64748b' },
    overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.06em', lineHeight: 1.5 },
  },
  shape: {
    borderRadius: 10,
  },
  shadows: [
    'none',
    '0 1px 3px rgb(0 0 0 / 0.04), 0 1px 2px rgb(0 0 0 / 0.02)',
    '0 1px 3px rgb(0 0 0 / 0.06), 0 1px 2px -1px rgb(0 0 0 / 0.06)',
    '0 4px 6px -1px rgb(0 0 0 / 0.06), 0 2px 4px -2px rgb(0 0 0 / 0.04)',
    '0 10px 15px -3px rgb(0 0 0 / 0.06), 0 4px 6px -4px rgb(0 0 0 / 0.04)',
    '0 20px 25px -5px rgb(0 0 0 / 0.06), 0 8px 10px -6px rgb(0 0 0 / 0.04)',
    ...Array(19).fill('0 25px 50px -12px rgb(0 0 0 / 0.15)'),
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: '"DM Sans", system-ui, -apple-system, sans-serif',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 500,
          borderRadius: 8,
          padding: '8px 18px',
          boxShadow: 'none',
          '&:hover': { boxShadow: '0 1px 3px rgb(0 0 0 / 0.06)' },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: '1px solid #e5e8ed',
          boxShadow: '0 1px 3px rgb(0 0 0 / 0.04), 0 1px 2px rgb(0 0 0 / 0.02)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.75rem',
          fontWeight: 500,
          borderRadius: 6,
          padding: '6px 12px',
        },
      },
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
      <ErrorBoundary>
      <AuthProvider>
        <NotificationProvider>
          <OrderCountsProvider>
            <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>Loading...</div>}>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/tracking" element={<TrackingSearchPage />} />
              <Route path="/tracking/:trackingNumber" element={<OrderTrackingPage />} />
              
              {/* 3PL Client Portal — protected but without the admin Layout */}
              <Route
                path="/3pl/portal"
                element={
                  <ProtectedRoute>
                    <ThreePLClientPortal />
                  </ProtectedRoute>
                }
              />

              {/* Protected Routes */}
              <Route 
                path="/*" 
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Routes>
                        {dashboardRoutes}
                        {orderRoutes}
                        {deliveryRoutes}
                        {adminRoutes}
                        {otherRoutes}
                        {threeplRoutes}
                        {hubRoutes}
                      </Routes>
                    </Layout>
                  </ProtectedRoute>
                }
              />
            </Routes>
            </Suspense>
          </OrderCountsProvider>
        </NotificationProvider>
      </AuthProvider>
      </ErrorBoundary>
      </Router>
    </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
